import {mapGetters} from 'vuex';
import {Reference} from 'smd-vue-scaffold';
import Routes from '@/router/routes';

import {MedicineService} from '@/services/medicine';

import StatusIcon from '@/components/common/status-icon';
import AttachedFileCard from '@/components/attached-file-card';
import PrescriptionCard from '@/components/prescription-card';
import OrderStatusText from '@/components/common/order-status-text';
import ModalServiceOrderCancel from '@/components/services/modal-service-order-cancel';
import ModalReportIssue from '@/components/services/modal-report-issue';
import ModalPreviewFile from '@/modals/modal-preview-file';
import ToastMixin from '@/mixins/toast-mixin';

import {formatDate} from '@/utils/dates';
import PreviewType from "@/reference/preview-file-type";
import {
  STATUS_CODES,
  STATUS_CODES_TEXT,
  ORDER_STATUS_CAN_CANCEL,
  PARTNER_ICONS,
  DEFAULT_PHARMACIES,
  ORDER_STATUS_NEEDS_PAYMENT,
} from '@/reference/medicine';

import {
  isOrderCancelled,
  orderStatusLabelId,
} from '@/utils/medicine';

import {
  GETTER_ACCOUNT_ID,
  GETTER_MEDICINE_PARTNER,
  GETTER_FILES_FROM_DOC,
} from '@/store/types';

export default {
  mixins: [
    ToastMixin,
  ],
  components: {
    StatusIcon,
    AttachedFileCard,
    PrescriptionCard,
    OrderStatusText,
  },
  filters: {
    formatCity(city) {
      return city.replace('\n', '').replace('\n', ', ');
    },
  },
  props: {
    serviceOrder: {type: Object, required: true},
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: GETTER_ACCOUNT_ID,
      getMedicinePartner: GETTER_MEDICINE_PARTNER,
      doctorFiles: GETTER_FILES_FROM_DOC,
    }),
    contentStylesClass() {
      return {'dark-light-bg d-medicine-order-details-default__content-style': this.$isDesktop}
    },
    orderNo() {
      if (!this.serviceOrder) return null;
      return this.serviceOrder.orderrefno || this.serviceOrder.orderid;
    },
    isPending() {
      return this.serviceOrder && this.serviceOrder.status === STATUS_CODES.PENDING;
    },
    isCancelled() {
      return this.serviceOrder && isOrderCancelled(this.serviceOrder.status);
    },
    showOrderStatusUpdateDate() {
      if (!this.serviceOrder) return false;

      const showStatus = [
        STATUS_CODES.PROCESSING,
        STATUS_CODES.DELIVERING,
        STATUS_CODES.FULFILLED,
      ];

      return showStatus.includes(this.serviceOrder.status);
    },
    statusTextLabelId() {
      if (!this.serviceOrder) return 'txt117';
      return orderStatusLabelId(this.serviceOrder.status);
    },
    statusDateTextColorClass() {
      if (!this.serviceOrder) return 'carrotOrange';

      if (this.isCancelled) return 'crimson';

      switch (this.serviceOrder.status) {
        case STATUS_CODES.PENDING:
          return 'carrotOrange';

        default:
          return 'mountainGreen';
      }
    },
    statusDateText() {
      return this.isCancelled
        ? formatDate(this.serviceOrder.daterequested, 'MMM D, YYYY [at] h:mm A')
        : formatDate(this.serviceOrder.daterequested, 'h:mm A, MMM D, YYYY');
    },
    cancellationReason() {
      return this.isCancelled ? STATUS_CODES_TEXT[this.serviceOrder.status] : null;
    },
    pharmacyPartner() {
      if (!this.serviceOrder) return null;
      return this.getMedicinePartner(this.serviceOrder.partnercode);
    },
    canCancel() {
      if (!this.serviceOrder) return false;
      return ORDER_STATUS_CAN_CANCEL.includes(this.serviceOrder.status);
    },
    isNSPharm() {
      if (!this.serviceOrder) return false;
      return this.serviceOrder.partnercode === DEFAULT_PHARMACIES.NS_PHARMACY;
    },
    isCancelledOrFulfilled() {
      if (!this.serviceOrder) return false;
      return this.serviceOrder.status === STATUS_CODES.FULFILLED || this.isCancelled;
    },
    isAccountThatOrdered() {
      if (!this.serviceOrder) return null;
      return this.accountId === this.serviceOrder.accountid;
    },
    needsPayment() {
      if (!this.serviceOrder) return false;
      return ORDER_STATUS_NEEDS_PAYMENT.includes(this.serviceOrder.status);
    },
    primaryButtonVariant() {
      return this.needsPayment ? 'red-enabled' : 'blue-enabled';
    },
    primaryButtonLabelId() {
      const defaultLabelId = 'txt219';

      if (!this.serviceOrder) return defaultLabelId;

      if (this.isNSPharm && !this.isCancelledOrFulfilled && this.isAccountThatOrdered) {
        return this.needsPayment ? 'txt197' : 'txt268';
      }

      return defaultLabelId;
    },
    showTotalPaymentAmount() {
      if (!this.serviceOrder) return false;

      if (!this.serviceOrder.amount) return false;

      if (this.isNSPharm) {
        return this.serviceOrder.status !== STATUS_CODES.PENDING && !this.isCancelled;
      }

      return true;
    },
    isConfirmedStatus() {
      if (!this.serviceOrder) return false;
      return this.serviceOrder.status === STATUS_CODES.CONFIRMED;
    },
    hasToken() {
      if (!this.serviceOrder) return false;
      return this.serviceOrder.token && this.serviceOrder.token !== '';
    },
    addedPrescriptions() {
      if (!this.serviceOrder) return [];

      return this.serviceOrder.prescriptions.length
        ? this.serviceOrder.prescriptions.map((prescription) => {
          const matchedDoctorFile = this.doctorFiles.find(({uploadid}) => prescription.uploadid === uploadid);
          const orderClaimedCoupons = this.coupons || [];
          const claimedCoupons = orderClaimedCoupons.filter((claimedCoupon) => {
            return claimedCoupon.uploadid === prescription.uploadid;
          });
          let mappedPrescription = {...prescription};

          if (matchedDoctorFile) {
            mappedPrescription = {...matchedDoctorFile, ...mappedPrescription};
          }

          if (claimedCoupons) {
            mappedPrescription.coupons = claimedCoupons;
          } else {
            delete mappedPrescription.coupons;
          }

          return mappedPrescription;
        })
        : this.serviceOrder.files.map((file) => ({
          uploadid: file,
          token: this.serviceOrder.token,
          remarks: this.serviceOrder.remarks,
        }));
    },
    ids() {
      if (!this.serviceOrder) return [];

      return this.serviceOrder.files.length
        ? this.serviceOrder.files.length > 1 ? this.serviceOrder.files.slice(1) : []
        : this.serviceOrder.ids;
    },
    patientName() {
      if (!this.serviceOrder) return 'Unnamed';

      return [this.serviceOrder.firstname, this.serviceOrder.middlename, this.serviceOrder.lastname]
        .filter((item) => !!item)
        .join(' ');
    },
    street() {
      return this.serviceOrder.address ? this.serviceOrder.address.street : null;
    },
    city() {
      return this.serviceOrder.address ? this.serviceOrder.address.city : null;
    },
  },
  methods: {
    cancelOrder() {
      this.$modal.open({
        component: ModalServiceOrderCancel,
        size: Reference.SmdModalSizes.sm,
        position: 'detached auto-height',
        props: {
          orderid: this.serviceOrder.orderid,
        },
        onClose: ({success}) => {
          if (success) {
            this.$emit('refetch');
          }
        }
      });
    },
    onPrimaryButtonClick() {
      if (!this.serviceOrder) {
        this.reportIssue();
      } else if (this.isNSPharm && !this.isCancelledOrFulfilled && this.isAccountThatOrdered) {
        this.redirectToMessaging();
      } else {
        this.reportIssue();
      }
    },
    reportIssue() {
      this.$modal.open({
        component: ModalReportIssue,
        size: Reference.SmdModalSizes.sm,
        position: 'detached auto-height',
        props: {
          orderid: this.serviceOrder.orderid,
        },
      });
    },
    redirectToMessaging() {
      this.$router.push({
        name: Routes.ORDER_CHAT_ROUTE,
        params: {
          [Routes.ORDER_CHAT_ROUTE_PARAM]: this.serviceOrder.orderid,
        },
      });
    },
    async viewId(id) {
      this.loading = true;

      try {
        const {data} = await MedicineService.getOrderFile(this.serviceOrder.orderid, id);

        if (data.url) {
          this.$modal.open({
            component: ModalPreviewFile,
            props: {
              file: [data.url],
              type: PreviewType.PREVIEW_TYPE_SC_PWD_ID_FILE,
              showButton: false
            },
          });
        } else {
          throw Error();
        }

        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.showToast({
          text: 'Failed to fetch order. Please try again later.',
          theme: 'danger',
        });
      }
    },
  },
  PARTNER_ICONS,
}
